import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.10 — Evidence Request Screen.
 * Shows additional evidence requests and responses.
 */
interface EvidenceRequest {
  readonly requestId: string;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly evidenceType: string;
  readonly description: string;
  readonly status: string;
  readonly responseUrl: string | null;
  readonly respondedAt: string | null;
  readonly remarks: string | null;
}

export function EvidenceRequestPage(): ReactNode {
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [entries, setEntries] = useState<EvidenceRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<EvidenceRequest[]>(`/v1/closure/review/${verificationId}/evidence`, { token: auth.token })
      .then((d) => { if (alive) setEntries(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load evidence requests'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="evidence-request"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!entries) return <main aria-label="evidence-request"><p>{t('closure.evidence.loading')}</p></main>;

  return (
    <main aria-label="evidence-request">
      <h2>{t('closure.evidence.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{t('closure.evidence.back')}</Link></p>

      {entries.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('closure.evidence.empty')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {entries.map((e) => (
            <div key={e.requestId} data-testid={`evidence-${e.requestId}`}
                 style={{ border: '1px solid var(--color-border-default)', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong>{e.evidenceType}</strong>
                <span style={{ fontSize: 12, color: e.status === 'PENDING' ? 'var(--color-danger)' : 'var(--color-success)' }}>{e.status}</span>
              </div>
              <p style={{ margin: '4px 0' }}>{e.description}</p>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                {t('closure.evidence.requestedBy')} {e.requestedBy} | {at(e.requestedAt)}
              </div>
              {e.respondedAt && (
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  {t('closure.evidence.responded')} {at(e.respondedAt)}
                  {e.remarks && <> — {e.remarks}</>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
